'use client';

import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, INLINES, Node, Block, MARKS } from '@contentful/rich-text-types';
import renderEmbeddedEntry from 'components/nodes/EmbeddedEntry';
import renderBlockEmbeddedEntry from 'components/nodes/BlockEmbeddedEntry';
import renderEntryHyperlink from 'components/nodes/EntryHyperlink';
import renderEmbeddedAsset from 'components/nodes/EmbeddedAsset';
import ResponsiveTable from 'components/nodes/ResponsiveTable';
import Link from 'components/shared/Link/Link';
import { RichTextCopyProps } from './types';
import React, { useMemo } from 'react';
import { getCleanDocument, getIdFromText, getTextFromChildren } from 'utils/formatters';
import { replaceDynamicTokens } from 'utils/dynamicTokens';
import { cn } from 'utils/styles';
import { renderSuperscript } from 'utils/superscriptUtils';
import { useDisclosureNumberingMap } from 'contexts/DisclosureProvider';
import { BulletListStyle } from 'types/constants';
import { getUnorderedListClasses, getOrderedListClasses, renderListItem } from 'utils/bulletListHelpers';

const defaultDynamicTokens: Record<string, string> = {
  currentDate: new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }),
};

const ySpacingMap = {
  default: 'my-4',
  none: 'my-0',
  sm: 'my-2',
  lg: 'my-4 sm:my-11',
};

const ySpacingParagraphMap = {
  default: 'peer-[.paragraph]:my-4',
  none: 'peer-[.paragraph]:my-0',
  sm: 'peer-[.paragraph]:my-2',
  lg: 'peer-[.paragraph]:my-11',
};

const RichTextCopy = React.memo(function RichTextCopy({
  copy,
  additionalOptions,
  ySpacing,
  arrowStyles,
  inline,
  showSimpleListStyle,
  bulletListStyle = BulletListStyle.Arrow,
}: RichTextCopyProps) {
  const disclosureNumberingMap = useDisclosureNumberingMap();

  const cleanCopy = useMemo(() => {
    if (!copy) return null;
    const doc = getCleanDocument(copy) as Document;
    return replaceDynamicTokens(doc, defaultDynamicTokens);
  }, [copy]);

  const renderOptions: Options = useMemo(() => {
    const ySpacingClass = ySpacingMap[ySpacing || 'default'];
    const ySpacingParagraphClass = ySpacingParagraphMap[ySpacing || 'default'];
    const baseHeadingClasses = cn('font-sans font-semibold', ySpacingClass);

    const createHeading = (level: number, classes: string) => {
      const HeadingComponent = (_node: Node, children: React.ReactNode) => {
        const headingText = getTextFromChildren(children);
        const Tag = `h${level}` as keyof JSX.IntrinsicElements;

        return (
          <Tag className={cn(baseHeadingClasses, classes)} id={getIdFromText(headingText)}>
            {children}
          </Tag>
        );
      };

      HeadingComponent.displayName = `Heading${level}`;
      return HeadingComponent;
    };

    const options: Options = {
      renderNode: {
        [BLOCKS.HEADING_2]: createHeading(2, 'text-heading-md my-11 mb-0'),
        [BLOCKS.HEADING_3]: createHeading(3, 'text-heading-sm mb-0'),
        [BLOCKS.HEADING_4]: createHeading(4, 'font-semibold'),
        [BLOCKS.HEADING_5]: createHeading(5, 'font-semibold'),
        [BLOCKS.HEADING_6]: createHeading(6, 'font-semibold'),
        [BLOCKS.QUOTE]: (node: Node, children: React.ReactNode) => (
          <blockquote
            className={cn(
              'font-sans font-[380] text-2xl md:text-[2rem] leading-none tracking-[-0.005em] text-content-primary my-12',
              '[&>p:first-child]:before:content-["“"] [&>p:last-child]:after:content-["”"]',
            )}>
            {children}
          </blockquote>
        ),
        [BLOCKS.HR]: () => <hr className={`${ySpacingClass} border-content-primary`} />,
        [BLOCKS.UL_LIST]: (node: Node, children: React.ReactNode) => {
          return <ul className={getUnorderedListClasses(showSimpleListStyle, bulletListStyle)}>{children}</ul>;
        },
        [BLOCKS.OL_LIST]: (node: Node, children: React.ReactNode) => (
          <ol className={getOrderedListClasses(showSimpleListStyle)}>{children}</ol>
        ),
        [BLOCKS.LIST_ITEM]: (node: Node, children: React.ReactNode) =>
          renderListItem(children, showSimpleListStyle, arrowStyles, bulletListStyle),
        [BLOCKS.PARAGRAPH]: (node: Node, children: React.ReactNode) => (
          <p className={cn('peer paragraph', ySpacingParagraphClass, 'last:mb-0', inline && 'inline m-0')}>
            {children}
          </p>
        ),
        [BLOCKS.TABLE]: (node: Node, children: React.ReactNode) => (
          <ResponsiveTable node={node as Block} renderOptions={options}>
            <tbody>{children}</tbody>
          </ResponsiveTable>
        ),
        [BLOCKS.TABLE_ROW]: (node: Node, children: React.ReactNode) => (
          <tr className={`border-b border-pebble`}>{children}</tr>
        ),
        [BLOCKS.TABLE_HEADER_CELL]: (node: Node, children: React.ReactNode) => (
          <th className="font-sans font-semibold p-4 align-top break-words text-base">{children}</th>
        ),
        [BLOCKS.TABLE_CELL]: (node: Node, children: React.ReactNode) => (
          <td className="p-4 align-top break-words text-base text-(--table-text-color)">{children}</td>
        ),
        [BLOCKS.EMBEDDED_ENTRY]: (node: Node, children: React.ReactNode) => {
          if (!copy) return null;
          return <div className={`${ySpacingClass}`}>{renderBlockEmbeddedEntry(node, children, copy)}</div>;
        },
        [BLOCKS.EMBEDDED_ASSET]: (node: Node, children: React.ReactNode) => {
          if (!copy) return null;
          return <div className={`${ySpacingClass}`}>{renderEmbeddedAsset(node, children, copy)}</div>;
        },
        [INLINES.HYPERLINK]: (node: Node, children: React.ReactNode) => {
          return (
            <Link
              href={node.data.uri}
              className={cn('inline underline hover:text-(--richtext-link-hover) focus:text-(--richtext-link-hover)')}
              data-testid="inline-hyperlink">
              {children}
            </Link>
          );
        },
        [INLINES.ENTRY_HYPERLINK]: (node: Node, children: React.ReactNode) => {
          if (!copy) return null;
          return renderEntryHyperlink(node, children, copy);
        },
        [INLINES.EMBEDDED_ENTRY]: (node: Node, children: React.ReactNode) => {
          if (!copy) return null;
          return renderEmbeddedEntry(node, children, copy, disclosureNumberingMap);
        },
      },
      renderMark: {
        [MARKS.SUPERSCRIPT]: (children: React.ReactNode) => renderSuperscript(children),
      },
      renderText: text => {
        return text.split('\n').reduce<React.ReactNode[]>((children, textSegment, index) => {
          return [...children, index > 0 && <br key={index} />, textSegment];
        }, []);
      },
      ...additionalOptions,
    };

    return options;
  }, [
    ySpacing,
    arrowStyles,
    inline,
    showSimpleListStyle,
    bulletListStyle,
    copy,
    additionalOptions,
    disclosureNumberingMap,
  ]);

  if (!cleanCopy) return null;

  return documentToReactComponents(cleanCopy, renderOptions);
});

export { RichTextCopy as default };
